/**
 * Утилиты для работы с библиотекой `@tanstack/react-query`
 */

import type { UseQueryOptions } from "@tanstack/react-query"

import { api } from "~/shared/api/api"
import { getErrorMessage } from "~/shared/lib/utils"

/**
 * Создает функцию для поля `queryFn` входного аргумента хука `useQuery` и его вариаций. Эта функция вызывает указанный api.
 * Предполагается, что api возвращает _JSON_.
 * @param apiUrl относительный url вызываемого api
 * @param dataKey имя поля в ответе api, значение которого нужно вернуть. Если не указано — возвращается весь ответ
 * @example
 *
 * import { useQuery } from "@tanstack/react-query"
 * import { createQueryFn } from "~/shared/api/react-query-utils"
 *
 * const query = useQuery({
 * 		queryKey: ["my-key"],
 * 		queryFn: createQueryFn('path/to/api', 'result')
 * 	})
 */
export function createQueryFn(apiUrl: string, dataKey: string | undefined = undefined) {
	return async () => {
		try {
			const res = await api.get(apiUrl)

			if (dataKey) return res.data[dataKey]
			return res.data

		} catch (err) {
			throw new Error(await getErrorMessage(err))
		}
	}
}

/**
 * Создает объект, который передается в хук `useQuery` и его вариации. В поле `queryFn` устанавливается функция, которая вызывает указанный api.
 * Предполагается, что api возвращает _JSON_.
 * @param apiUrl относительный url вызываемого api
 * @param dataKey имя поля в ответе api, значение которого нужно вернуть. Если не указано — возвращается весь ответ
 * @param queryKey уникальный ключ. Если не указан, будет использоваться `apiUrl`
 * @example
 *
 * import { useQuery } from "@tanstack/react-query"
 * import { createApiQuery } from "~/shared/api/react-query-utils"
 *
 * const myQuery = createApiQuery("path/to/api", "result", "my-query")
 *
 * function useMyQuery(){
 *     return useQuery(myQuery)
 * }
 */
export function createApiQuery(apiUrl: string, dataKey: string | undefined = undefined, queryKey: string | undefined = undefined) {
	return {
		queryKey: [queryKey ?? apiUrl],
		queryFn: createQueryFn(apiUrl, dataKey)
	} satisfies UseQueryOptions
}

/**
 * Создает фабричную функцию, с помощью которой создается объект, который передается в хук `useQuery` и его вариации.
 * В поле `queryFn` устанавливается функция, которая вызывает указанный api. Предполагается, что api возвращает _JSON_.
 * Создаваемая фабричная функция принимает в качестве аргумента объект, который преобразится в строку с search-параметрами.
 * Search-параметры также будут использоваться в поле `queryKey`.
 * @param apiUrl относительный url вызываемого api
 * @param dataKey имя поля в ответе api, значение которого нужно вернуть. Если не указано — возвращается весь ответ
 * @param queryKey уникальный ключ. Если не указан, будет использоваться `apiUrl`
 *
 * @example
 *
 * import { useQuery } from "@tanstack/react-query"
 * import { createApiQueryWithSearchParams } from "~/shared/api/react-query-utils"
 *
 * const myQuery = createApiQueryWithSearchParams("path/to/api", "result", "my-query")
 *
 * function useMyQuery(){
 *     return useQuery(myQuery({ foo: "bar" }))
 * }
 */
export function createApiQueryWithSearchParams<SP extends {
	[key: string]: string | number | boolean | null | undefined
}>(apiUrl: string, dataKey: string | undefined = undefined, queryKey: string | undefined = undefined) {
	return (searchParams: SP) => ({
		queryKey: [queryKey ?? apiUrl, ...Object.values(searchParams)],
		queryFn: createQueryFn(`${apiUrl}?${Object.entries(searchParams).map(entry => entry.join("=")).join("&")}`, dataKey)
	} satisfies UseQueryOptions)
}